import { useState, useEffect, useCallback } from "react";
import { getLiveVehicles, MOCK_DEPARTURES } from "../utils/bods";

/**
 * Poll live vehicle positions every 15 seconds. These are SIRI-VM vehicle
 * positions from BODS, not stop departures.
 */
export function useLiveVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const doFetch = useCallback(async () => {
    try {
      const data = await getLiveVehicles();
      setVehicles(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError("Live vehicles unavailable");
      setVehicles([]);
    }
  }, []);

  useEffect(() => {
    let interval = null;
    const timer = setTimeout(() => {
      doFetch();
      interval = setInterval(doFetch, 15000);
    }, 500);

    return () => {
      clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [doFetch]);

  return { vehicles, error, lastUpdated };
}

async function fetchStopServices(atco) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 65000);
  try {
    const res = await fetch(`/api/stop-services?atco=${encodeURIComponent(atco)}`, { signal: controller.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.error || `Stop services failed: ${res.status}`);
    return data;
  } finally {
    clearTimeout(timer);
  }
}

function serviceRowsToBoardRows(services) {
  return services.map((service, index) => ({
    route: service.route,
    dest: service.destination || "See timetable",
    via: service.operatorName || service.operatorRef || "Verified from BODS timetable",
    operatorName: service.operatorName || service.operatorRef || "",
    nextTimes: service.nextTimes || service.scheduledTimes || [],
    time: service.nextTimes?.[0]?.time || service.scheduledTimes?.[0]?.time || "Timetable",
    mins: service.nextTimes?.[0]?.minutes ?? service.scheduledTimes?.[0]?.minutes ?? 999,
    serviceOnly: true,
    serviceType: service.serviceType || "Normal",
    key: `${service.route}-${service.destination}-${service.operatorName || service.operatorRef || index}`,
    destinations: service.destinations || []
  }));
}

/**
 * Gets exact services for a stop using the server-side BODS timetable index.
 * This avoids guessed buses. If BODS timetable indexing fails, we fall back only
 * to explicitly verified local demo rows, never area guesses.
 */
export function useDepartures(stop) {
  const [departures, setDepartures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("none");
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const stopId = typeof stop === "string" ? stop : stop?.id;

  useEffect(() => {
    let cancelled = false;
    if (!stopId) return undefined;

    setLoading(true);
    setError(null);
    setMeta(null);

    fetchStopServices(stopId)
      .then((data) => {
        if (cancelled) return;
        setMeta(data.meta || null);
        if (data.services?.length) {
          setDepartures(serviceRowsToBoardRows(data.services));
          setSource("bods-timetable");
        } else {
          const localRows = MOCK_DEPARTURES[stopId] || [];
          setDepartures(localRows);
          setSource(localRows.length ? "verified-demo" : "none");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const localRows = MOCK_DEPARTURES[stopId] || [];
        setDepartures(localRows);
        setSource(localRows.length ? "verified-demo" : "none");
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stopId]);

  return { departures, loading, source, error, meta };
}
